import React, { useState, useRef, useEffect } from 'react';
import { X, Send, Sparkles, MessageCircle, Mic, MicOff, Volume2, VolumeX, Loader2 } from 'lucide-react';
import { askNyansa, getSuggestedQuestions } from '../../services/nyansaService';
import { showToast } from '../../utils/toast';

interface NyansaPanelProps {
  onClose: () => void;
  currentSection: string;
  userName?: string;
  isAuthenticated?: boolean;
}

interface Message {
  id: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  timestamp: Date;
}

const NyansaPanel: React.FC<NyansaPanelProps> = ({ 
  onClose, 
  currentSection,
  userName = 'there',
  isAuthenticated = false
}) => {
  const [input, setInput] = useState('');
  const [messages, setMessages] = useState<Message[]>([
    {
      id: 'welcome',
      role: 'assistant',
      content: `Hello ${userName}! I'm Nyansa, your health assistant. How can I help you today?`,
      timestamp: new Date()
    }
  ]);
  const [isLoading, setIsLoading] = useState(false);
  const [isMicActive, setIsMicActive] = useState(false);
  const [isSpeakerActive, setIsSpeakerActive] = useState(false);
  const [suggestedQuestions, setSuggestedQuestions] = useState<string[]>([]);
  
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  
  // Get context-aware suggested questions
  useEffect(() => {
    const contextMap: Record<string, string> = {
      'hub': 'default',
      'community': 'community',
      'discover': 'drug-info',
      'spotlight': 'expert',
      'shop': 'checkout',
      'profile': 'default'
    };
    
    const context = contextMap[currentSection] || 'default';
    setSuggestedQuestions(getSuggestedQuestions(context));
  }, [currentSection]);
  
  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);
  
  // Focus input on mount
  useEffect(() => {
    inputRef.current?.focus();
  }, []);
  
  // Handle form submission
  const handleSubmit = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    
    if (!input.trim()) return;
    
    // Add user message
    const userMessage: Message = {
      id: `user-${Date.now()}-${Math.random().toString(36).substring(2, 7)}`,
      role: 'user',
      content: input.trim(),
      timestamp: new Date()
    };
    
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setIsLoading(true);
    
    try {
      // Format chat history for the AI
      const chatHistory = messages.map(msg => ({
        role: msg.role,
        content: msg.content
      }));
      
      // Get context based on current section
     const contextMap: Record<string, string> = {
  hub: "browsing the personalized Health Hub, which includes the user's activity feed, real-time expert updates, quick access to favorite content, and shortcut actions to ask health questions or join ongoing community discussions.",

  community: "exploring Community Echoes, a space where users read, post, and engage in health-related conversations. This section includes polls, reactions, threaded discussions, and the ability to tag experts or follow trending topics.",

  discover: "browsing the Health Cosmos, which contains expertly written health articles and a comprehensive drug dictionary. Users can search naturally for medical terms, symptoms, or drugs and access simplified summaries or AI-lens breakdowns.",

  spotlight: "browsing Expert Spotlights, where verified health professionals host live Q&A sessions, publish thought leadership content, and provide booking links. Users can follow experts, reserve seats for sessions, or ask direct questions.",

  shop: "shopping in the Unified Health Marketplace, which includes over-the-counter (OTC) products and expert-recommended items. Users can search by health category, filter by expert seller, and complete purchases via secure checkout systems like Stripe and Mobile Money.",

  profile: "managing personal account settings, preferences, health history, chat records, reserved expert sessions, and following lists. This area also includes access to user achievements, saved posts, and notification preferences."
};

      
      // Get AI response
      const response = await askNyansa({
        userInput: input.trim(),
        context: contextMap[currentSection] || '',
        userName: userName,
        chatHistory
      });
      
      // Add AI response
      const aiMessage: Message = {
        id: `assistant-${Date.now()}-${Math.random().toString(36).substring(2, 7)}`,
        role: 'assistant',
        content: response.success ? response.text : 'Sorry, I encountered an error. Please try again later.',
        timestamp: new Date()
      };
      
      setMessages(prev => [...prev, aiMessage]);
      
      // Read aloud if speaker is active
      if (isSpeakerActive && response.success) {
        speakText(response.text);
      }
    } catch (error) {
      console.error('Error getting AI response:', error);
      showToast.system.error('Failed to get a response from Nyansa');
      
      // Add error message
      const errorMessage: Message = {
        id: `error-${Date.now()}`,
        role: 'system',
        content: 'Sorry, I encountered an error. Please try again later.',
        timestamp: new Date()
      };
      
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };
  
  // Handle suggested question click
  const handleSuggestedQuestionClick = (question: string) => {
    setInput(question);
    
    // Focus the input
    inputRef.current?.focus();
    
    // Submit after a short delay to show the question in the input
    setTimeout(() => {
      handleSubmit();
    }, 300);
  };
  
  // Handle microphone toggle
  const handleMicToggle = () => {
    if (!isMicActive) {
      // Request microphone permission
      navigator.mediaDevices.getUserMedia({ audio: true })
        .then(stream => {
          setIsMicActive(true);
          showToast.system.info('Microphone activated. Speak your question.');
          
          // TODO: Implement speech recognition
          // For now, just simulate speech recognition
          setTimeout(() => {
            setIsMicActive(false);
            setInput('What are the common side effects of paracetamol?');
            
            // Submit after a short delay
            setTimeout(() => {
              handleSubmit();
            }, 500);
          }, 3000);
        })
        .catch(err => {
          console.error('Error accessing microphone:', err);
          showToast.system.error('Could not access microphone. Please check permissions.');
        });
    } else {
      setIsMicActive(false);
    }
  };
  
  // Handle speaker toggle
  const handleSpeakerToggle = () => {
    setIsSpeakerActive(!isSpeakerActive);
  };
  
  // Text-to-speech function
  const speakText = (text: string) => {
    if ('speechSynthesis' in window) {
      // Remove markdown and other formatting
      const cleanText = text
        .replace(/\*\*(.*?)\*\*/g, '$1') // Remove bold
        .replace(/\_(.*?)\_/g, '$1') // Remove italic
        .replace(/\[(.*?)\]\((.*?)\)/g, '$1') // Remove links
        .replace(/\n\n/g, '. ') // Replace double newlines with period + space
        .replace(/\n/g, ' ') // Replace single newlines with space
        .replace(/\s+/g, ' ') // Replace multiple spaces with single space
        .replace(/\_Disclaimer.*/, ''); // Remove disclaimer
      
      const utterance = new SpeechSynthesisUtterance(cleanText);
      utterance.lang = 'en-US';
      utterance.rate = 0.9; // Slightly slower
      utterance.pitch = 1;
      
      window.speechSynthesis.speak(utterance);
    }
  };
  
  // Handle backdrop click
  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };
  
  // Format message content with basic markdown
  const formatMessage = (content: string) => {
    return content
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>') // Bold
      .replace(/\_(.*?)\_/g, '<em>$1</em>') // Italic
      .replace(/\n\n/g, '<br/><br/>') // Double newlines
      .replace(/\n/g, '<br/>'); // Single newlines
  };
  
  return (
    <div 
      className="fixed inset-0 bg-black/30 backdrop-blur-sm flex items-center justify-center z-50 p-4 md:p-0"
      onClick={handleBackdropClick}
    >
      <div 
        className="w-full md:w-[480px] h-[600px] max-h-[90vh] bg-white/70 dark:bg-gray-900/80 backdrop-blur-md rounded-2xl shadow-2xl flex flex-col animate-scale-in"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-blue-500 rounded-full flex items-center justify-center">
              <Sparkles className="text-white" size={20} />
            </div>
            <div>
              <h3 className="font-bold text-gray-900 dark:text-gray-100">Nyansa</h3>
              <p className="text-xs text-gray-600 dark:text-gray-400">Your Health Companion</p>
            </div>
          </div>
          
          <div className="flex items-center space-x-2">
            <button
              onClick={handleSpeakerToggle}
              className={`p-2 rounded-full transition-colors ${
                isSpeakerActive 
                  ? 'bg-green-100 dark:bg-green-900/50 text-green-600 dark:text-green-400' 
                  : 'text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'
              }`}
              aria-label={isSpeakerActive ? 'Disable text-to-speech' : 'Enable text-to-speech'}
            >
              {isSpeakerActive ? <Volume2 size={18} /> : <VolumeX size={18} />}
            </button>
            
            <button
              onClick={onClose}
              className="p-2 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full transition-colors"
              aria-label="Close"
            >
              <X size={18} />
            </button>
          </div>
        </div>
        
        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4 scrollbar-custom">
          {messages.map((message) => (
            <div 
              key={message.id} 
              className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'} ${
                message.role === 'system' ? 'justify-center' : ''
              }`}
            >
              {message.role === 'system' ? (
                <div className="bg-yellow-50 dark:bg-yellow-900/30 border border-yellow-200 dark:border-yellow-800 rounded-lg p-3 max-w-[80%]">
                  <p className="text-yellow-800 dark:text-yellow-300 text-sm">{message.content}</p>
                </div>
              ) : (
                <div 
                  className={`max-w-[80%] ${
                    message.role === 'user' 
                      ? 'bg-[#F1F1F1] dark:bg-gray-700 rounded-2xl rounded-br-md' 
                      : 'bg-white/90 dark:bg-gray-800/80 border border-gray-200 dark:border-gray-700 rounded-2xl rounded-bl-md shadow-sm'
                  } p-4`}
                >
                  {message.role === 'assistant' && (
                    <div className="flex items-center space-x-2 mb-2">
                      <Sparkles size={14} className="text-green-500 dark:text-green-400" />
                      <span className="text-xs font-medium text-green-600 dark:text-green-400">Nyansa AI</span>
                    </div>
                  )}
                  
                  <div 
                    className={`text-sm ${
                      message.role === 'user' 
                        ? 'text-gray-800 dark:text-gray-200' 
                        : 'text-gray-700 dark:text-gray-300'
                    }`}
                    dangerouslySetInnerHTML={{ __html: formatMessage(message.content) }}
                  />
                  
                  <div className="mt-1 text-right">
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                      {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </span>
                  </div>
                </div>
              )}
            </div>
          ))}
          
          {/* Loading indicator */}
          {isLoading && (
            <div className="flex justify-start">
              <div className="bg-white/90 dark:bg-gray-800/80 border border-gray-200 dark:border-gray-700 rounded-2xl rounded-bl-md shadow-sm p-4 max-w-[80%]">
                <div className="flex items-center space-x-2 mb-2">
                  <Sparkles size={14} className="text-green-500 dark:text-green-400" />
                  <span className="text-xs font-medium text-green-600 dark:text-green-400">Nyansa AI</span>
                </div>
                
                <div className="flex items-center space-x-2">
                  <div className="flex space-x-1">
                    <div className="w-2 h-2 bg-gray-400 dark:bg-gray-600 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                    <div className="w-2 h-2 bg-gray-400 dark:bg-gray-600 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                    <div className="w-2 h-2 bg-gray-400 dark:bg-gray-600 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                  </div>
                  <span className="text-sm text-gray-500 dark:text-gray-400">Thinking...</span>
                </div>
              </div>
            </div>
          )}
          
          <div ref={messagesEndRef} />
        </div>
        
        {/* Suggested Questions */}
        {suggestedQuestions.length > 0 && messages.length < 3 && (
          <div className="px-4 py-3 border-t border-gray-200 dark:border-gray-700">
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">Suggested Questions:</p>
            <div className="flex flex-wrap gap-2">
              {suggestedQuestions.map((question, index) => (
                <button
                  key={index}
                  onClick={() => handleSuggestedQuestionClick(question)}
                  className="text-sm bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 px-3 py-1.5 rounded-full hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                >
                  {question}
                </button>
              ))}
            </div>
          </div>
        )}
        
        {/* Input */}
        <div className="p-4 border-t border-gray-200 dark:border-gray-700">
          <form onSubmit={handleSubmit} className="flex items-center space-x-2">
            <div className="relative flex-1">
              <input
                ref={inputRef}
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Ask Nyansa anything..."
                className="input-base pr-10 w-full dark:bg-gray-800 dark:border-gray-700"
                disabled={isLoading || isMicActive}
              />
              
              <button
                type="button"
                onClick={handleMicToggle}
                className={`absolute right-3 top-1/2 transform -translate-y-1/2 p-1 rounded-full transition-colors ${
                  isMicActive 
                    ? 'bg-red-100 dark:bg-red-900/50 text-red-600 dark:text-red-400 animate-pulse' 
                    : 'text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300'
                }`}
              >
                {isMicActive ? <MicOff size={18} /> : <Mic size={18} />}
              </button>
            </div>
            
            <button
              type="submit"
              disabled={!input.trim() || isLoading || isMicActive}
              className={`p-3 rounded-full transition-colors ${
                !input.trim() || isLoading || isMicActive
                  ? 'bg-gray-200 dark:bg-gray-700 text-gray-400 dark:text-gray-500 cursor-not-allowed'
                  : 'bg-green-600 text-white hover:bg-green-700'
              }`}
            >
              {isLoading ? <Loader2 className="animate-spin" size={18} /> : <Send size={18} />}
            </button>
          </form>
          
          <div className="mt-2 text-center">
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Nyansa provides general information only. Always consult a healthcare professional.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NyansaPanel;